// ============================================================================
// Cartes Gestion (F-771/772/773/774/775, F-778) — responsable, plans, contrats,
// documents, statut d'emploi
// ============================================================================
// M-769 : options responsable = actifs de l'equipe (+ directeur), fournies par le
// DTO (rosterOptions + target.teamDirector*). M-751 : la demande d'inactivation
// sur soi est refusee serveur ; le hub n'affiche le bouton que si can.requestInactivation.
// F-774/778 : DOCUMENTS — upload cable (lot UPLOADS). Download toujours attachment.

import { useRef, useState } from 'react';
import { ConfirmDialog, GS_COMPONENTS } from '@gs/ui';
import { FileText, Plus, Trash2, Download, UserMinus } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { Card, CardHeader, Badge, DataRow, EmptyRow, Modal, ModalFooter } from './ui';
import { downloadBlobAs } from '../../lib/blobs/objectUrl';
import {
  managementErrorCode, useSetManager, useCreateCareerPlan, useUpdateCareerPlanStatus, useDeleteCareerPlan,
  useIssueContract, useUpdateContractStatus, useRequestInactivation,
  useUploadEmployeeDocument, useDeleteEmployeeDocument,
  type ManagementTargetDto, type ManagerOption, type CareerPlan, type EmployeeContract,
  type ContractTemplate, type EmployeeDocument, type PendingInactivation,
} from '../../hooks/useManagement';

type OnError = (code: string) => void;
const label = 'text-meta font-medium text-slate-600 dark:text-slate-300';
const STATUS = ['pending', 'in_progress', 'done'] as const;

// ─── F-771 Responsable hierarchique ──────────────────────────────────────────

export function ManagerForm({ email, target, options, onError }: {
  email: string; target: ManagementTargetDto; options: ManagerOption[]; onError: OnError;
}) {
  const { t } = useTranslation();
  const setManager = useSetManager();
  const [value, setValue] = useState(target.managerEmail ?? '');
  const director = target.teamDirectorEmail;
  const opts = [...options];
  if (director && director !== target.email && !opts.some((o) => o.email === director)) opts.unshift({ email: director, fullName: target.teamDirectorName });
  const defaultLabel = director && director !== target.email ? t('gest:management.manager.defaultDirector', { name: target.teamDirectorName ?? director }) : t('gest:management.manager.defaultRoot');

  function submit() {
    setManager.mutate({ email, payload: { managerEmail: value || null } }, { onError: (x) => onError(managementErrorCode(x)) });
  }

  return (
    <Card>
      <CardHeader title={t('gest:management.manager.title')} />
      <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="mgr-select" className={label}>{t('gest:management.manager.responsable')}</label>
          <select id="mgr-select" value={value} onChange={(e) => setValue(e.target.value)} className={`${GS_COMPONENTS.input} w-64`}>
            <option value="">{defaultLabel}</option>
            {opts.map((o) => <option key={o.email} value={o.email}>{o.fullName ?? o.email}</option>)}
          </select>
        </div>
        <button type="submit" disabled={setManager.isPending} className={GS_COMPONENTS.btnAction}>{t('gest:management.common.save')}</button>
      </form>
      <p className="mt-2 text-chip text-slate-500 dark:text-slate-400">{t('gest:management.manager.hint')}</p>
    </Card>
  );
}

// ─── F-772 Plans & parcours ───────────────────────────────────────────────────

export function CareerPlansCard({ email, plans, onError }: { email: string; plans: CareerPlan[]; onError: OnError }) {
  const { t } = useTranslation();
  const create = useCreateCareerPlan();
  const setStatus = useUpdateCareerPlanStatus();
  const del = useDeleteCareerPlan();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<'pip' | 'parcours'>('parcours');
  const [title, setTitle] = useState('');
  const [milestones, setMilestones] = useState('');
  const [err, setErr] = useState<string | null>(null);

  function submit() {
    if (!title.trim()) { setErr(t('gest:management.errors.invalid')); return; }
    setErr(null);
    create.mutate({ email, payload: { kind, title: title.trim(), milestones: milestones.trim() || null } }, {
      onSuccess: () => { setOpen(false); setTitle(''); setMilestones(''); },
      onError: (x) => onError(managementErrorCode(x)),
    });
  }

  return (
    <Card>
      <CardHeader title={t('gest:management.plans.title')} action={<button type="button" onClick={() => setOpen(true)} className={`${GS_COMPONENTS.btnSecondary} inline-flex items-center gap-1.5`}><Plus size={16} aria-hidden="true" />{t('gest:management.plans.new')}</button>} />
      {plans.length === 0 ? <p className="text-field text-slate-500 dark:text-slate-400">{t('gest:management.plans.empty')}</p> : (
        <ul className="flex flex-col gap-2">
          {plans.map((p) => (
            <li key={p.id} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2"><Badge tone={p.status === 'done' ? 'success' : p.status === 'in_progress' ? 'info' : 'neutral'}>{t(`gest:management.status.${p.status}`)}</Badge><p className="font-medium text-slate-700 dark:text-slate-200">{t(`gest:management.plans.kind.${p.kind}`)} · {p.title}</p></div>
                <div className="flex items-center gap-1.5">
                  <select value={p.status} onChange={(e) => setStatus.mutate({ email, payload: { planId: p.id, status: e.target.value } }, { onError: (x) => onError(managementErrorCode(x)) })} aria-label={t('gest:management.plans.statusLabel')} className={`${GS_COMPONENTS.input} h-8 py-0`}>{STATUS.map((s) => <option key={s} value={s}>{t(`gest:management.status.${s}`)}</option>)}</select>
                  <button type="button" onClick={() => del.mutate({ email, payload: { planId: p.id } }, { onError: (x) => onError(managementErrorCode(x)) })} className={`${GS_COMPONENTS.btnGhost} text-red-600 dark:text-red-400`}>{t('gest:management.common.delete')}</button>
                </div>
              </div>
              {p.milestones.length > 0 ? <ul className="ml-4 mt-1 list-disc text-field text-slate-500 dark:text-slate-400">{[...p.milestones].sort((a, b) => a.sortOrder - b.sortOrder).map((m) => <li key={m.id}>{m.title}</li>)}</ul> : null}
            </li>
          ))}
        </ul>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={t('gest:management.plans.dialogTitle')}>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5"><label htmlFor="plan-kind" className={label}>{t('gest:management.plans.kindLabel')}</label><select id="plan-kind" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)} className={GS_COMPONENTS.input}><option value="parcours">{t('gest:management.plans.kind.parcours')}</option><option value="pip">{t('gest:management.plans.kind.pip')}</option></select></div>
          <div className="flex flex-col gap-1.5"><label htmlFor="plan-title" className={label}>{t('gest:management.plans.titleLabel')}</label><input id="plan-title" maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} className={GS_COMPONENTS.input} /></div>
        </div>
        <div className="flex flex-col gap-1.5"><label htmlFor="plan-ms" className={label}>{t('gest:management.plans.milestones')}</label><textarea id="plan-ms" rows={4} value={milestones} onChange={(e) => setMilestones(e.target.value)} className={GS_COMPONENTS.input} /></div>
        {err ? <p role="alert" className="text-field text-red-600 dark:text-red-400">{err}</p> : null}
        <ModalFooter onCancel={() => setOpen(false)} cancelLabel={t('gest:management.common.cancel')} submitLabel={t('gest:management.plans.submit')} onSubmit={submit} pending={create.isPending} />
      </Modal>
    </Card>
  );
}

// ─── F-773 Contrats ───────────────────────────────────────────────────────────

const CONTRACT_STATUS = ['draft', 'issued', 'signed'] as const;

export function ContractsCard({ email, contracts, templates, onError }: { email: string; contracts: EmployeeContract[]; templates: ContractTemplate[]; onError: OnError }) {
  const { t } = useTranslation();
  const issue = useIssueContract();
  const setStatus = useUpdateContractStatus();
  const [open, setOpen] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [err, setErr] = useState<string | null>(null);

  function submit() {
    if (!title.trim()) { setErr(t('gest:management.errors.invalid')); return; }
    if (!templateId && !body.trim()) { setErr(t('gest:management.errors.invalid')); return; }
    setErr(null);
    issue.mutate({ email, payload: { templateId: templateId || null, title: title.trim(), body: body.trim() || null } }, {
      onSuccess: () => { setOpen(false); setTitle(''); setBody(''); },
      onError: (x) => onError(managementErrorCode(x)),
    });
  }

  return (
    <Card>
      <CardHeader title={t('gest:management.contracts.title')} action={templates.length > 0 ? <button type="button" onClick={() => setOpen(true)} className={`${GS_COMPONENTS.btnSecondary} inline-flex items-center gap-1.5`}><FileText size={16} aria-hidden="true" />{t('gest:management.contracts.issue')}</button> : undefined} />
      {templates.length === 0 ? <p className="text-field text-slate-500 dark:text-slate-400">{t('gest:management.contracts.noTemplates')}</p> : null}
      {contracts.length === 0 ? <p className="text-field text-slate-500 dark:text-slate-400">{t('gest:management.contracts.empty')}</p> : (
        <ul className="flex flex-col gap-2">
          {contracts.map((c) => (
            <li key={c.id}><DataRow leading={<Badge tone={c.status === 'signed' ? 'success' : c.status === 'issued' ? 'info' : 'neutral'}>{t(`gest:management.contracts.status.${c.status}`)}</Badge>} title={c.title} subtitle={c.createdAt.slice(0, 10)} trailing={
              <select value={c.status} onChange={(e) => setStatus.mutate({ email, payload: { contractId: c.id, status: e.target.value } }, { onError: (x) => onError(managementErrorCode(x)) })} aria-label={t('gest:management.contracts.statusLabel')} className={`${GS_COMPONENTS.input} h-8 py-0`}>{CONTRACT_STATUS.map((s) => <option key={s} value={s}>{t(`gest:management.contracts.status.${s}`)}</option>)}</select>
            } /></li>
          ))}
        </ul>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={t('gest:management.contracts.dialogTitle')} size="lg">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5"><label htmlFor="ct-template" className={label}>{t('gest:management.contracts.template')}</label><select id="ct-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)} className={GS_COMPONENTS.input}><option value="">{t('gest:management.contracts.noTemplate')}</option>{templates.map((tp) => <option key={tp.id} value={tp.id}>{tp.name}</option>)}</select></div>
          <div className="flex flex-col gap-1.5"><label htmlFor="ct-title" className={label}>{t('gest:management.contracts.titleLabel')}</label><input id="ct-title" maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} className={GS_COMPONENTS.input} /></div>
        </div>
        <div className="flex flex-col gap-1.5"><label htmlFor="ct-body" className={label}>{t('gest:management.contracts.body')}</label><textarea id="ct-body" rows={5} value={body} onChange={(e) => setBody(e.target.value)} className={GS_COMPONENTS.input} /></div>
        {err ? <p role="alert" className="text-field text-red-600 dark:text-red-400">{err}</p> : null}
        <ModalFooter onCancel={() => setOpen(false)} cancelLabel={t('gest:management.common.cancel')} submitLabel={t('gest:management.contracts.submit')} onSubmit={submit} pending={issue.isPending} />
      </Modal>
    </Card>
  );
}

// ─── F-774/778 Documents employe (lot UPLOADS) ───────────────────────────────

const DOC_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,application/pdf';
const DOC_MAX_BYTES = 10 * 1024 * 1024;

export function EmployeeDocumentsSection({
  email,
  documents,
  canManage,
  onError,
}: {
  email: string;
  documents: EmployeeDocument[];
  canManage: boolean;
  onError: OnError;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadEmployeeDocument(email);
  const remove = useDeleteEmployeeDocument(email);
  const [kind, setKind] = useState<'nova' | 'gallup' | 'autre'>('autre');
  const [confirmId, setConfirmId] = useState<string | null>(null);

  function onPick(file: File | undefined): void {
    if (!file) return;
    if (file.size > DOC_MAX_BYTES) { onError('too_large'); return; }
    if (!DOC_ACCEPT.split(',').includes(file.type)) { onError('type_refused'); return; }
    upload.mutate({ file, kind }, { onError: (err) => onError(managementErrorCode(err)) });
  }

  return (
    <Card>
      <CardHeader
        title={t('gest:management.documents.title')}
        action={
          canManage ? (
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as 'nova' | 'gallup' | 'autre')}
                className={GS_COMPONENTS.input}
                aria-label={t('gest:management.documents.kindLabel')}
              >
                <option value="nova">{t('gest:management.documents.kind.nova')}</option>
                <option value="gallup">{t('gest:management.documents.kind.gallup')}</option>
                <option value="autre">{t('gest:management.documents.kind.autre')}</option>
              </select>
              <input
                ref={inputRef}
                type="file"
                accept={DOC_ACCEPT}
                className="sr-only"
                onChange={(e) => {
                  onPick(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                disabled={upload.isPending}
                onClick={() => inputRef.current?.click()}
                className={`${GS_COMPONENTS.btnSecondary} inline-flex items-center gap-1.5 touch-manipulation`}
              >
                <Plus size={16} aria-hidden="true" />
                {upload.isPending ? t('gest:management.documents.uploading') : t('gest:management.documents.upload')}
              </button>
            </div>
          ) : null
        }
      />
      {documents.length === 0 ? (
        <EmptyRow title={t('gest:management.documents.empty')} />
      ) : (
        <ul className="flex flex-col gap-1.5">
          {documents.map((d) => (
            <li key={d.id} className="flex items-center gap-2 rounded-md border border-slate-200 px-2.5 py-1.5 text-field dark:border-slate-700">
              <FileText size={16} aria-hidden="true" className="shrink-0 text-slate-400" />
              <Badge tone="neutral">{t(`gest:management.documents.kind.${d.kind}`, d.kind)}</Badge>
              <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">{d.fileName}</span>
              <span className="shrink-0 text-chip text-slate-500 dark:text-slate-400">{t('gest:management.documents.size', { size: Math.max(1, Math.round(d.sizeBytes / 1024)) })}</span>
              <button
                type="button"
                className={`${GS_COMPONENTS.btnSecondary} touch-manipulation px-2 py-1`}
                onClick={() => {
                  void downloadBlobAs(
                    `/api/management/${encodeURIComponent(email)}/documents/${d.id}/download`,
                    d.fileName,
                  ).then((ok) => { if (!ok) onError('server'); });
                }}
                aria-label={t('gest:management.documents.download')}
              >
                <Download size={14} aria-hidden="true" />
              </button>
              {canManage ? (
                <button
                  type="button"
                  className={`${GS_COMPONENTS.btnDangerOutline} touch-manipulation px-2 py-1`}
                  onClick={() => setConfirmId(d.id)}
                  aria-label={t('gest:management.documents.remove')}
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        open={confirmId !== null}
        title={t('gest:management.documents.confirmTitle')}
        message={t('gest:management.documents.confirmMessage')}
        confirmLabel={t('gest:management.documents.confirmConfirm')}
        cancelLabel={t('gest:management.documents.confirmCancel')}
        variant="danger"
        isLoading={remove.isPending}
        onConfirm={() => {
          if (!confirmId) return;
          remove.mutate(confirmId, {
            onSuccess: () => setConfirmId(null),
            onError: (err) => { setConfirmId(null); onError(managementErrorCode(err)); },
          });
        }}
        onCancel={() => setConfirmId(null)}
      />
    </Card>
  );
}

// ─── F-775 Statut d'emploi (demande de mise en inactif) ──────────────────────

export function InactivationRequestCard({ email, target, pending, canRequest, onError }: {
  email: string; target: ManagementTargetDto; pending: PendingInactivation | null; canRequest: boolean; onError: OnError;
}) {
  const { t } = useTranslation();
  const request = useRequestInactivation();
  const [open, setOpen] = useState(false);
  const [lastDay, setLastDay] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);

  function submit() {
    if (!lastDay) { setErr(t('gest:management.inactivation.dateRequired')); return; }
    if (!reason.trim()) { setErr(t('gest:management.inactivation.reasonRequired')); return; }
    setErr(null);
    request.mutate({ email, payload: { lastActiveDay: lastDay, reason: reason.trim() } }, {
      onSuccess: () => { setOpen(false); setLastDay(''); setReason(''); },
      onError: (x) => onError(managementErrorCode(x)),
    });
  }

  return (
    <Card>
      <CardHeader title={t('gest:management.inactivation.title')} action={<Badge tone={target.isEmployed ? 'success' : 'neutral'}>{target.isEmployed ? t('gest:management.inactivation.active') : t('gest:management.inactivation.inactive')}</Badge>} />
      {!target.isEmployed ? (
        <p className="text-field text-slate-500 dark:text-slate-400">{t('gest:management.inactivation.alreadyInactive')}</p>
      ) : pending ? (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-900 dark:bg-blue-950/40">
          <p className="text-field text-blue-900 dark:text-blue-200">{t('gest:management.inactivation.pendingTitle')}</p>
          <p className="mt-1 text-field text-blue-800 dark:text-blue-300">{t('gest:management.inactivation.pendingBody', { date: pending.lastActiveDay, name: pending.requestedByName ?? '' })}</p>
          {pending.reason ? <p className="mt-1 text-field text-blue-800 dark:text-blue-300">{t('gest:chrome.quoted', { text: pending.reason })}</p> : null}
        </div>
      ) : canRequest ? (
        <>
          <p className="mb-2 text-field text-slate-600 dark:text-slate-300">{t('gest:management.inactivation.hint')}</p>
          <button type="button" onClick={() => setOpen(true)} className={`${GS_COMPONENTS.btnSecondary} inline-flex items-center gap-1.5`}><UserMinus size={16} aria-hidden="true" />{t('gest:management.inactivation.request')}</button>
        </>
      ) : (
        <p className="text-field text-slate-500 dark:text-slate-400">{t('gest:management.inactivation.adminOnly')}</p>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={t('gest:management.inactivation.dialogTitle')}>
        <p className="text-field text-slate-600 dark:text-slate-300">{t('gest:management.inactivation.dialogHint')}</p>
        <div className="flex flex-col gap-1.5"><label htmlFor="ir-date" className={label}>{t('gest:management.inactivation.lastDay')}</label><input id="ir-date" type="date" value={lastDay} onChange={(e) => setLastDay(e.target.value)} className={`${GS_COMPONENTS.input} sm:max-w-52`} /></div>
        <div className="flex flex-col gap-1.5"><label htmlFor="ir-reason" className={label}>{t('gest:management.inactivation.reason')}</label><textarea id="ir-reason" rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className={GS_COMPONENTS.input} /></div>
        {err ? <p role="alert" className="text-field text-red-600 dark:text-red-400">{err}</p> : null}
        <ModalFooter onCancel={() => setOpen(false)} cancelLabel={t('gest:management.common.cancel')} submitLabel={t('gest:management.inactivation.submit')} onSubmit={submit} pending={request.isPending} />
      </Modal>
    </Card>
  );
}
